import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';
import {
  AUTH_QUERY_KEY,
  fetchAuthStatus,
  useAuth,
  useInvalidateAuth,
} from '@/lib/auth/useAuth';
import AuthShell from './AuthShell';

/**
 * Login page. Renders the dummy-mode user/password form; Cognito mode hands
 * off to the Flask hosted UI rather than rendering anything here.
 *
 * Flow:
 *   1. Fetch `/auth/config`. Missing/4xx → treat config as unknown and fall
 *      back to dummy login (a broken config endpoint never blocks login).
 *   2. If already authenticated, bounce to the `next` target.
 *   3. If `use_cognito_auth` is true, full-page redirect to `/cognito/login`.
 *   4. Otherwise, render the dummy login form POSTing to `/dummy/login`.
 */

interface AuthConfig {
  use_cognito_auth: boolean;
}

function useAuthConfig() {
  return useQuery<AuthConfig>({
    queryKey: ['auth', 'config'],
    // Broken/missing endpoint must never block login — fall back to dummy.
    queryFn: async () => {
      try {
        return await apiGet<AuthConfig>(endpoints.auth.config(), { redirectOn401: false });
      } catch {
        return { use_cognito_auth: false };
      }
    },
    staleTime: Infinity,
    retry: 0,
  });
}

function sanitizeNext(raw: string | null): string {
  if (!raw) return '/';
  if (!raw.startsWith('/') || raw.startsWith('//')) return '/';
  return raw;
}

export default function LoginPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const next = sanitizeNext(searchParams.get('next'));
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const invalidateAuth = useInvalidateAuth();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { data: config, isLoading: isConfigLoading, error: configError } = useAuthConfig();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthLoading && isAuthenticated) {
      navigate(next, { replace: true });
    }
  }, [isAuthenticated, isAuthLoading, navigate, next]);

  useEffect(() => {
    if (isAuthLoading || isAuthenticated) return;
    if (!config?.use_cognito_auth) return;
    window.location.assign(
      endpoints.auth.cognitoLogin(next !== '/' ? { next } : undefined),
    );
  }, [config?.use_cognito_auth, isAuthenticated, isAuthLoading, next]);

  async function handleDummyLogin(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    const form = event.currentTarget;
    const formData = new FormData(form);

    try {
      await fetch(endpoints.auth.dummyLogin(), {
        method: 'POST',
        credentials: 'include',
        body: formData,
        redirect: 'follow',
      });
    } catch {
      setError('Network error. Please try again.');
      return;
    }

    // Flask returns HTML for both success and bad-credentials — probe session
    // via /auth/status rather than trust the response body.
    await invalidateAuth();
    const status = await queryClient.fetchQuery({
      queryKey: AUTH_QUERY_KEY,
      queryFn: fetchAuthStatus,
      staleTime: 0,
    });

    if (!status.authenticated) {
      setError('Invalid credentials.');
      return;
    }

    await queryClient.invalidateQueries();
    navigate(next, { replace: true });
  }

  if (isAuthLoading || isConfigLoading) {
    return <CenteredMessage>Loading…</CenteredMessage>;
  }

  if (configError) {
    return (
      <CenteredMessage>
        Unable to reach the authentication service. Try refreshing the page.
      </CenteredMessage>
    );
  }

  if (config?.use_cognito_auth) {
    return <CenteredMessage>Redirecting to single sign-on…</CenteredMessage>;
  }

  return <DummyLoginForm next={next} error={error} onSubmit={handleDummyLogin} />;
}

/**
 * Matches `templates/auth/login.html` inline CSS 1:1. Uses a scoped
 * <style> block so `:hover` / `:focus` states work natively without
 * per-render JS handlers.
 */
function DummyLoginForm({
  next,
  error,
  onSubmit,
}: {
  next: string;
  error: string | null;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
}): JSX.Element {
  return (
    <AuthShell>
      <style>{LOGIN_CSS}</style>
      <div className="auth-login-title">Client&apos;s Portal</div>
      <div className="auth-login-subtitle">&nbsp;</div>

      <form
        method="POST"
        action={endpoints.auth.dummyLogin()}
        onSubmit={onSubmit}
        className="auth-login-form"
      >
        <input type="hidden" name="next" value={next} />

        <div className="auth-form-group">
          <label htmlFor="username">Username</label>
          <input id="username" name="username" type="text" required />
        </div>

        <div className="auth-form-group">
          <label htmlFor="password">Password</label>
          <input id="password" name="password" type="password" required />
        </div>

        <button type="submit" className="auth-login-btn">
          Login
        </button>

        {error ? (
          <div role="alert" className="auth-error-message">
            {error}
          </div>
        ) : null}
      </form>
    </AuthShell>
  );
}

/**
 * Scoped styles lifted verbatim from `templates/auth/login.html`.
 * Hex kept as-is when no `--*` var maps; `#F9FAFB` uses `var(--light-bg)`.
 */
const LOGIN_CSS = `
.auth-login-title {
  color: #23272F;
  font-size: 1.15rem;
  font-weight: 600;
  margin: 0 0 0.2rem 0;
  text-align: center;
  letter-spacing: 0.01em;
}
.auth-login-subtitle {
  color: #4B5563;
  font-size: 0.98rem;
  font-weight: 400;
  margin: 0 0 1.1rem 0;
  text-align: center;
  letter-spacing: 0.01em;
}
.auth-login-form {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.auth-form-group {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}
.auth-form-group label {
  font-weight: 500;
  color: #1F2937;
  font-size: 0.92rem;
}
.auth-form-group input {
  padding: 0.6rem 0.7rem;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  font-size: 0.97rem;
  background: var(--light-bg);
  transition: border-color 0.2s;
}
.auth-form-group input:focus {
  outline: none;
  border-color: #4B5563;
}
.auth-login-btn {
  background: #4B5563;
  color: var(--text-light);
  padding: 0.7rem 0;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;
  margin-top: 0.2rem;
}
.auth-login-btn:hover {
  background: #374151;
  transform: translateY(-1px);
}
.auth-error-message {
  color: #991B1B;
  margin-top: 0.7rem;
  text-align: center;
  font-size: 0.85rem;
}
`;

function CenteredMessage({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div
      className="flex min-h-screen items-center justify-center text-sm"
      style={{
        background: 'var(--light-bg)',
        color: '#4B5563',
        fontFamily: 'var(--font-body)',
      }}
    >
      {children}
    </div>
  );
}
